import React from "react";
import { useTranslation } from "react-i18next";

import Heading from "@components/layout/Heading";
import ConfirmationDetail, {
  ConfirmData,
} from "@components/shared/ConfirmationDetail";
import ConfirmModal from "@components/shared/ConfirmModal";
import WorkflowComplete from "@components/shared/WorkflowComplete";
import { isValidationError } from "@utils/formError";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";
import { DraftNote } from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { mergeDraftNoteIntoRow } from "@components/notes/mergeDraftNoteIntoRow";
import { DBTables } from "@utils/constants/dbTables";

type ConfirmMeterProps = {
  workflowInstanceIds: string[];
  /**
   * Entity mutation handler. May return the new/updated meter id so the
   * confirm component can flush an optional NoteStep draft afterwards. If a
   * draft is present but no id is returned, the draft is silently skipped.
   */
  onConfirm: () => Promise<string | void>;
  onCancel: () => void;
  isComplete: boolean;
  data: ConfirmData[];
  onPrevious: () => void;
  onEdit: (step: number) => void;
  /** Optional draft from the wizard's NoteStep; flushed after onConfirm resolves. */
  draftNote?: DraftNote;
  /** i18n key used in the flush-failure warning toast (e.g. "common.meter"). */
  noteArtifactNameKey?: string;
  /** DBTables value identifying the reference table for the note (e.g. DBTables.Meters). */
  noteReferenceTable?: DBTables;
};

const ConfirmMeter: React.FunctionComponent<ConfirmMeterProps> = ({
  workflowInstanceIds,
  onConfirm,
  onCancel,
  isComplete,
  data,
  onPrevious,
  onEdit,
  draftNote,
  noteArtifactNameKey,
  noteReferenceTable,
}) => {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const meterId = await onConfirm();

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Note failure does NOT roll back the entity; user gets a warning
      // toast and can re-add via the audit-trail panel.
      const noteResult =
        draftNote && noteReferenceTable && meterId
          ? await flushNoteStepDraft(draftNote, noteReferenceTable, meterId)
          : { kind: "skipped" as const };

      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", {
            artifact: t(noteArtifactNameKey || "common.meter"),
          }),
        );
      } else {
        toastSuccess(t("meter.toast.success"));
      }
    } catch (error: any) {
      const errorMessages: string[] = isValidationError(error)
        ? error.response?.data?.errors.map((i: any) => i.message)
        : [];

      toastError(
        <>
          <p>
            {t("meter.toast.failure", {
              error: error?.response?.data?.message || error?.message,
            })}
          </p>
          {errorMessages.length ? (
            <ul className="list-disc pl-4">
              {errorMessages.map((text: any) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
          ) : null}
        </>
      );
    }
    setIsLoading(false);
    setShowConfirmModal(false);
  };

  if (isComplete && workflowInstanceIds.length) {
    return <WorkflowComplete references={workflowInstanceIds} />;
  }

  // Merge the draft note into the LAST data row (conventionally evidence) so
  // its data-index → step-index alignment is preserved and Edit navigates
  // back to the shared note+evidence step.
  const confirmData =
    data.length > 0
      ? [
          ...data.slice(0, -1),
          mergeDraftNoteIntoRow(data[data.length - 1], draftNote, t),
        ]
      : data;

  return (
    <>
      <div className="flex flex-col gap-4 grow">
        <div className="p-6 space-y-4">
          <Heading light>{t("meter.adding_meter.confirm")}</Heading>
          <ConfirmationDetail data={confirmData} onEdit={onEdit} />
        </div>

        <div className="grow" />

        <footer className="flex gap-4 p-6 border-t border-gray-200">
          <button
            type="button"
            className="btn-outline-primary"
            onClick={onPrevious}
          >
            {t("common.prev_step")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setShowConfirmModal(true);
            }}
          >
            {t("common.ledger")}
          </button>
          <button
            type="button"
            className="btn-outline-primary"
            onClick={onCancel}
          >
            {t("common.cancel")}
          </button>
        </footer>
      </div>

      <ConfirmModal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
        }}
        onConfirm={handleConfirm}
        isSubmitting={isLoading}
      >
        {t("meter.adding_meter.confirm_message")}
      </ConfirmModal>
    </>
  );
};

export default ConfirmMeter;
